import { combineReducers } from 'redux';

import { EditorAction, EditorActionKeys } from './actions';
import { IndesignTextFieldsConfig } from './utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from './utils/htmlEditor/editableTextFields/getData';
import { TextFieldsEditableData } from './utils/editableTextFields/textFieldsEditableData';
import { IndesignMediaElementConfig } from './utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from './utils/htmlEditor/editableMedia/getData';
import { LinkConfig } from './utils/editableLinks/getData';
import { getRoundedValue } from './utils/helpers';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import UserAgentUtils from '../../utils/userAgent';
import SelectionManager from '../../utils/selectionManager';
import { EditorType } from './constants';

interface LabelState {
	label: string;
	isError: boolean;
	isRenaming: boolean;
}

interface SliderState {
	page: number;
	prevPage: number;
	totalPages: number;
}

interface PathsState {
	pagePath: string;
	imagePath: string;
	thumbnailPath: string;
	cssPath?: string;
	configsPath?: string;
	jsPath?: string;
}

export interface PageData {
	page: number;
	defaultHtml: string;
	html: string;
	width?: number;
	height?: number;
	fontsPath?: string;
	googleFontsPath?: string;
}

export interface PageDataState {
	[k: number]: PageData;
}

export interface ResourcesState {
	css: string;
	scripts?: string;
}

export interface EditableData {
	modifiedFromBackoffice?: boolean;
	textFieldsData?: TextFieldsEditableData;
}

export interface EditableDataState {
	[k: number]: EditableData;
}

export interface BackgroundMediaData {
	globalId: number;
	label: string;
	relUrl: string;
	type: Api.AssetItemFileTypes;
}

export interface BgStyleData {
	epubStyle: {
		backgroundMedia: BackgroundMediaData;
		defaultBackgroundMedia?: BackgroundMediaData;
		backgroundImage?: BackgroundMediaData; // legacy option
		backgroundColor?: string; // isn't handled in Editor but can be set in Plugin
	};
}

export interface ConfigsData {
	generalData: {
		bgStyle: BgStyleData | {};
		editablePublication: EditableDataState;
	};
	pagesData: {
		bgStyle: BgStyleData | {};
		isTransparent: boolean;
		height: number;
	}[];
}

export interface EditableTextFieldsState {
	[k: number]: {
		[k: string]: IndesignTextFieldsConfig | HtmlTextFieldsConfig;
	};
}

export interface EditableMediaState {
	[k: number]: {
		[k: string]: IndesignMediaElementConfig | HtmlMediaElementConfig;
	};
}

export interface EditableLinksState {
	[k: number]: {
		[k: string]: LinkConfig
	};
}

interface EditPanelState {
	isDisplayed: boolean;
	x: number | null;
	y: number | null;
}

interface ActiveItemState {
	id: string;
	parentId: string | null;
}

interface ComboState {
	isCombining: boolean;
	percentage: number;
}

interface PageSelectionState {
	isActive: boolean;
	pages: number[];
	lastSelectedPage: number | null;
	lastSelectedShiftPage: number | null;
	shouldDisplayNotification: boolean;
}

export interface EditorState {
	type: EditorType;
	label: LabelState;
	slider: SliderState;
	paths: PathsState;
	pages: PageDataState;
	previewPages: number[];
	resources: ResourcesState;
	configs: ConfigsData;
	editableTextFields: EditableTextFieldsState;
	editableMedia: EditableMediaState;
	editableLinks: EditableLinksState;
	isEditorLoaded: boolean;
	isThumbnailsDisplayed: boolean;
	editPanel: EditPanelState;
	thumbnails: Api.EditorThumbnailData[];
	activeItem: ActiveItemState;
	combo: ComboState;
	version: number;
	pageSelection: PageSelectionState;
	pageZoom: number;
	isPageBackgroundActive: boolean;
}

export enum ZoomType {
	OUT = 'OUT',
	IN = 'IN',
	FIT = 'FIT',
}

const INITIAL_PAGE = 1;
const INITIAL_TOTAL_PAGES = 1;
const DEFAULT_WIDTH = 1000;
const DEFAULT_HEIGHT = 1000;
const ZOOM_STEP = 0.2;

const labelInitialState: LabelState = {
	label: '',
	isError: false,
	isRenaming: false,
};

const sliderInitialState: SliderState = {
	page: INITIAL_PAGE,
	prevPage: INITIAL_PAGE,
	totalPages: INITIAL_TOTAL_PAGES,
};

const pathsInitialState: PathsState = {
	pagePath: '',
	cssPath: '',
	jsPath: '',
	configsPath: '',
	imagePath: '',
	thumbnailPath: '',
};

const getPageDefaultData = (page: number, width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT): PageData => ({
	page,
	defaultHtml: '',
	html: '',
	width,
	height,
	fontsPath: '',
	googleFontsPath: '',
});

const pagesInitialState: PageDataState = {
	[INITIAL_PAGE]: getPageDefaultData(INITIAL_PAGE),
};

const previewPagesInitialState: number[] = [];

const resourcesInitialState: ResourcesState = {
	css: '',
};

const configsDataInitialState: ConfigsData = {
	generalData: {
		bgStyle: {},
		editablePublication: {},
	},
	pagesData: [],
};

const editableTextFieldsInitialState: EditableTextFieldsState = {
	[INITIAL_PAGE]: {},
};

const editableMediaInitialState: EditableMediaState = {
	[INITIAL_PAGE]: {},
};

const editableLinksInitialState: EditableLinksState = {
	[INITIAL_PAGE]: {},
};

const editPanelInitialState: EditPanelState = {
	isDisplayed: false,
	x: null,
	y: null,
};

const thumbnailsInitialState: Api.EditorThumbnailData[] = [];

const activeItemInitialState: ActiveItemState = {
	id: '',
	parentId: null,
};

const comboInitialState: ComboState = {
	isCombining: false,
	percentage: 0,
};

const pageSelectionInitialState: PageSelectionState = {
	isActive: false,
	pages: [],
	lastSelectedPage: null,
	lastSelectedShiftPage: null,
	shouldDisplayNotification: UserAgentUtils.isTouchSupported(),
};

const type = (state: EditorType = EditorType.HTML, action: EditorAction): EditorType => {
	switch (action.type) {
		case EditorActionKeys.SET_EDITOR_TYPE:
			return action.editorType;
		case EditorActionKeys.RESET:
			return EditorType.HTML;
		default:
			return state;
	}
};

const label = (state: LabelState = labelInitialState, action: EditorAction): LabelState => {
	switch (action.type) {
		case EditorActionKeys.SET_DATA:
			return {
				...state,
				label: action.data.label,
			};
		case EditorActionKeys.SET_IS_RENAMING:
			return {
				...state,
				isRenaming: action.isRenaming,
			};
		case EditorActionKeys.RENAME:
			return {
				...state,
				label: action.newLabel,
				isRenaming: false,
			};
		case EditorActionKeys.SET_RENAME_ERROR:
			return {
				...state,
				isError: action.isError,
			};
		case EditorActionKeys.RESET:
			return labelInitialState;
		default:
			return state;
	}
};

const slider = (state: SliderState = sliderInitialState, action: EditorAction): SliderState => {
	switch (action.type) {
		case EditorActionKeys.SET_DATA: {
			if ('totalPages' in action.data) {
				return {
					...state,
					totalPages: action.data.totalPages,
				};
			} else {
				return state;
			}
		}
		case EditorActionKeys.CHANGE_PAGE:
			return {
				...state,
				page: action.page,
				prevPage: state.page,
			};
		case EditorActionKeys.RESET:
			return sliderInitialState;
		default:
			return state;
	}
};

const paths = (state: PathsState = pathsInitialState, action: EditorAction): PathsState => {
	switch (action.type) {
		case EditorActionKeys.SET_DATA: {
			if ('imagePath' in action.data) {
				const {
					pagePath,
					cssPath,
					jsPath,
					configsPath,
					imagePath,
					thumbnailPath,
				} = action.data;
				return {
					...state,
					pagePath,
					cssPath,
					jsPath,
					configsPath,
					imagePath,
					thumbnailPath,
				};
			} else {
				const {
					pagePath,
				} = action.data;
				return {
					...state,
					pagePath,
				};
			}
		}
		case EditorActionKeys.RESET:
			return pathsInitialState;
		default:
			return state;
	}
};

const pages = (state: PageDataState = pagesInitialState, action: EditorAction): PageDataState => {
	switch (action.type) {
		case EditorActionKeys.ADD_PAGE: {
			const { page, width, height } = action;
			return {
				...state,
				[page]: getPageDefaultData(page, width, height),
			};
		}
		case EditorActionKeys.SET_PAGE_DATA:
			return {
				...state,
				[action.data.page]: {
					...state[action.data.page],
					...action.data,
				},
			};
		case EditorActionKeys.RESET:
			return pagesInitialState;
		default:
			return state;
	}
};

const previewPages = (state: number[] = previewPagesInitialState, action: EditorAction): number[] => {
	switch (action.type) {
		case EditorActionKeys.SET_DATA: {
			if ('nonEpubPages' in action.data) {
				const { nonEpubPages } = action.data;
				return nonEpubPages
					? [...state, ...nonEpubPages]
					: state;
			} else {
				return state;
			}
		}
		case EditorActionKeys.RESET:
			return previewPagesInitialState;
		default:
			return state;
	}
};

const resources = (state: ResourcesState = resourcesInitialState, action: EditorAction): ResourcesState => {
	switch (action.type) {
		case EditorActionKeys.SET_RESOURCES_DATA:
			return {
				...state,
				...action.data,
			};
		case EditorActionKeys.RESET:
			return resourcesInitialState;
		default:
			return state;
	}
};

const configs = (state: ConfigsData = configsDataInitialState, action: EditorAction): ConfigsData => {
	switch (action.type) {
		case EditorActionKeys.SET_CONFIGS_DATA:
			return {
				...state,
				...action.data,
			};
		case EditorActionKeys.SET_EDITABLE_DATA: {
			const newState = Utils.deepCopy(state);
			const { page, data } = action;

			newState.generalData.editablePublication[page] = {
				...newState.generalData.editablePublication[page],
				...data,
			};

			return newState;
		}
		case EditorActionKeys.SET_PAGE_BACKGROUND: {
			const newState = Utils.deepCopy(state);
			const { page, data, isGeneralBgData } = action;
			const target = newState.pagesData[page - 1];
			const source = isGeneralBgData ? newState.generalData : target;

			if (data) {
				const {
					epubStyle: {
						defaultBackgroundMedia,
						backgroundMedia,
						backgroundImage,
					},
				} = source.bgStyle as BgStyleData;

				if (!defaultBackgroundMedia) {
					if (Utils.isEmptyObject(target.bgStyle)) {
						target.bgStyle = {
							epubStyle: {
								defaultBackgroundMedia: backgroundMedia || backgroundImage,
							},
						};
					} else {
						(target.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia = backgroundMedia || backgroundImage;
					}
				}

				// remove legacy option
				if (backgroundImage) {
					delete (target.bgStyle as BgStyleData).epubStyle.backgroundImage;
				}

				(target.bgStyle as BgStyleData).epubStyle.backgroundMedia = data;
			} else {
				(target.bgStyle as BgStyleData).epubStyle.backgroundMedia = (source.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia as BackgroundMediaData;
				delete (target.bgStyle as BgStyleData).epubStyle.defaultBackgroundMedia;
			}

			return newState;
		}
		case EditorActionKeys.RESET:
			return configsDataInitialState;
		default:
			return state;
	}
};

const editableTextFields = (
	state: EditableTextFieldsState = editableTextFieldsInitialState,
	action: EditorAction
): EditableTextFieldsState => {
	switch (action.type) {
		case EditorActionKeys.SET_EDITABLE_TEXT_FIELDS_DATA:
			return {
				...state,
				[action.page]: {
					...state[action.page],
					...Utils.itemsToById(action.data),
				},
			};
		case EditorActionKeys.UPDATE_EDITABLE_TEXT_FIELD:
			return {
				...state,
				[action.page]: {
					...state[action.page],
					[action.id]: {
						...state[action.page][action.id],
						...action.data,
						isEditedFromBackoffice: true,
					},
				},
			};
		case EditorActionKeys.RESET:
			return editableTextFieldsInitialState;
		default:
			return state;
	}
};

const editableMedia = (
	state: EditableMediaState = editableMediaInitialState,
	action: EditorAction
): EditableMediaState => {
	switch (action.type) {
		case EditorActionKeys.SET_EDITABLE_MEDIA_DATA:
			return {
				...state,
				[action.page]: {
					...state[action.page],
					...Utils.itemsToById(action.data),
				},
			};
		case EditorActionKeys.UPDATE_EDITABLE_MEDIA:
			return {
				...state,
				[action.page]: {
					...state[action.page],
					[action.id]: {
						...state[action.page][action.id],
						...action.data,
					},
				},
			};
		case EditorActionKeys.RESET:
			return editableMediaInitialState;
		default:
			return state;
	}
};

const editableLinks = (
	state: EditableLinksState = editableLinksInitialState,
	action: EditorAction
	): EditableLinksState => {
	switch (action.type) {
		case EditorActionKeys.SET_EDITABLE_LINKS_DATA:
			return {
				...state,
				[action.page]: {
					...state[action.page],
					...Utils.itemsToById(action.data),
				},
			};
		case EditorActionKeys.UPDATE_EDITABLE_LINK:
			return {
				...state,
				[action.page]: {
					...state[action.page],
					[action.id]: {
						...state[action.page][action.id],
						...action.data,
						isEditedFromBackoffice: true,
					},
				},
			};
		case EditorActionKeys.RESET:
			return editableLinksInitialState;
		default:
			return state;
	}
};

const isEditorLoaded = (state: boolean = false, action: EditorAction): boolean => {
	switch (action.type) {
		case EditorActionKeys.SET_IS_EDITOR_LOADED:
			return action.isLoaded;
		case EditorActionKeys.RESET:
			return false;
		default:
			return state;
	}
};

const isThumbnailsDisplayed = (state: boolean = true, action: EditorAction): boolean => {
	switch (action.type) {
		case EditorActionKeys.TOGGLE_THUMBNAILS:
			return !state;
		case EditorActionKeys.RESET:
			return true;
		default:
			return state;
	}
};

const editPanel = (state: EditPanelState = editPanelInitialState, action: EditorAction): EditPanelState => {
	switch (action.type) {
		case EditorActionKeys.TOGGLE_EDIT_PANEL:
			return {
				...state,
				isDisplayed: !state.isDisplayed,
			};
		case EditorActionKeys.RESET_ACTIVE_ITEM:
			return {
				...state,
				isDisplayed: false,
			};
		case EditorActionKeys.SET_IS_PAGE_BACKGROUND_ACTIVE:
			return {
				...state,
				isDisplayed: action.isActive,
			};
		case EditorActionKeys.MOVE_EDIT_PANEL:
			return {
				...state,
				x: action.x,
				y: action.y,
			};
		case EditorActionKeys.RESET:
			return editPanelInitialState;
		default:
			return state;
	}
};

const thumbnails = (state: Api.EditorThumbnailData[] = thumbnailsInitialState, action: EditorAction): Api.EditorThumbnailData[] => {
	switch (action.type) {
		case EditorActionKeys.SET_THUMBNAILS:
			return [
				...state,
				...action.data,
			];
		case EditorActionKeys.RESET:
			return thumbnailsInitialState;
		default:
			return state;
	}
};

const activeItem = (state: ActiveItemState = activeItemInitialState, action: EditorAction): ActiveItemState => {
	switch (action.type) {
		case EditorActionKeys.SET_ACTIVE_ITEM: {
			const { id, parentId } = action;
			return {
				...state,
				id,
				parentId,
			};
		}
		case EditorActionKeys.RESET_ACTIVE_ITEM:
		case EditorActionKeys.RESET:
			return activeItemInitialState;
		default:
			return state;
	}
};

const combo = (state: ComboState = comboInitialState, action: EditorAction): ComboState => {
	switch (action.type) {
		case EditorActionKeys.CREATE_COMBO:
			return {
				...state,
				isCombining: true,
			};
		case EditorActionKeys.UPDATE_COMBO_PERCENTAGE:
			return {
				...state,
				percentage: action.percentage,
			};
		case EditorActionKeys.FINISH_COMBO:
		case EditorActionKeys.RESET:
			return comboInitialState;
		default:
			return state;
	}
};

const version = (state: number = 0, action: EditorAction): number => {
	switch (action.type) {
		case EditorActionKeys.UPDATE_VERSION:
			return state + 1;
		default:
			return state;
	}
};

const pageSelection = (state: PageSelectionState = pageSelectionInitialState, action: EditorAction): PageSelectionState => {
	switch (action.type) {
		case EditorActionKeys.SET_PAGE_SELECTION:
			return {
				...state,
				isActive: true,
				pages: [action.currentPage],
				lastSelectedPage: action.currentPage,
				shouldDisplayNotification: false,
			};
		case EditorActionKeys.SELECT_PAGE: {
			// used for touch screens or when ctrl is pressed
			const getSelectedPages = (prevPages: number[], selectedPage: number): number[] => (
				prevPages.includes(selectedPage)
					? prevPages.filter(page => page !== selectedPage)
					: [...prevPages, selectedPage].sort((a, b) => a - b)
			);

			// do not handle ctrl/shift keys on touch screens
			if (UserAgentUtils.isTouchSupported()) {
				return {
					...state,
					pages: getSelectedPages(state.pages, action.page),
				};
			}

			let selectedPages: number[] = [];
			let lastSelectedPage: number | null = null;
			let lastSelectedShiftPage: number | null = null;

			if (action.isCtrlPressed) {
				selectedPages = getSelectedPages(state.pages, action.page);
				lastSelectedPage = action.page;
			} else if (action.isShiftPressed) {
				if (state.pages.length === 0) {
					selectedPages = [action.page];
					lastSelectedPage = action.page;
				} else {
					const allPages = Array.from({ length: action.totalPages }, (v, i) => i + 1);
					SelectionManager.set(state.pages, allPages);

					if (state.lastSelectedShiftPage) {
						SelectionManager.deselectItems(state.lastSelectedShiftPage, state.lastSelectedPage);
					}

					SelectionManager.selectItems(action.page, state.lastSelectedPage);

					selectedPages = SelectionManager.get().sort((a, b) => a - b);
					lastSelectedPage = state.lastSelectedPage;
					lastSelectedShiftPage = action.page;
				}
			} else {
				selectedPages = [action.page];
				lastSelectedPage = action.page;
			}

			return {
				...state,
				pages: selectedPages,
				lastSelectedPage,
				lastSelectedShiftPage,
			};
		}
		case EditorActionKeys.RESTORE_PAGE_SELECTION:
			return {
				...state,
				isActive: true,
				pages: action.pages,
				lastSelectedPage: action.pages[0],
			};
		case EditorActionKeys.RESET_PAGE_SELECTION:
		case EditorActionKeys.RESET:
			return {
				...pageSelectionInitialState,
				shouldDisplayNotification: false,
			};
		default:
			return state;
	}
};

const pageZoom = (state: number = 1, action: EditorAction): number => {
	switch (action.type) {
		case EditorActionKeys.ZOOM_PAGE: {
			if (action.zoom === ZoomType.FIT) {
				return 1;
			} else {
				const step = action.zoom === ZoomType.IN ? ZOOM_STEP : -ZOOM_STEP;
				const newValue = getRoundedValue(state += step, 10);
				return newValue < ZOOM_STEP ? ZOOM_STEP : newValue;
			}
		}
		case EditorActionKeys.RESET:
			return 1;
		default:
			return state;
	}
};

const isPageBackgroundActive = (state: boolean = false, action: EditorAction): boolean => {
	switch (action.type) {
		case EditorActionKeys.SET_IS_PAGE_BACKGROUND_ACTIVE:
			return action.isActive;
		case EditorActionKeys.RESET:
			return false;
		default:
			return state;
	}
};

export const editor = combineReducers<EditorState>({
	type,
	label,
	slider,
	paths,
	pages,
	previewPages,
	resources,
	configs,
	editableTextFields,
	editableMedia,
	editableLinks,
	isEditorLoaded,
	isThumbnailsDisplayed,
	editPanel,
	thumbnails,
	activeItem,
	combo,
	version,
	pageSelection,
	pageZoom,
	isPageBackgroundActive,
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/reducer.ts